import React, { ReactElement, useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { GRAPH_STATUSES } from '@Constants/constants';
import { TaskGraphDataPoint } from '../../types';

interface NivoTaskCompletionBarProps {
    data: TaskGraphDataPoint[];
    onSegmentClick: (dataPoint: TaskGraphDataPoint) => void;
}

const statusOrder: string[] = [
    GRAPH_STATUSES.NotApplicable.label,
    GRAPH_STATUSES.Complete.label,
    GRAPH_STATUSES.PastDue.label,
    GRAPH_STATUSES.InProgress.label,
    GRAPH_STATUSES.NotDue.label,
];

const statusColors: Record<string, string> = {
    [GRAPH_STATUSES.NotApplicable.label]: GRAPH_STATUSES.NotApplicable.color,
    [GRAPH_STATUSES.Complete.label]: GRAPH_STATUSES.Complete.color,
    [GRAPH_STATUSES.PastDue.label]: GRAPH_STATUSES.PastDue.color,
    [GRAPH_STATUSES.InProgress.label]: GRAPH_STATUSES.InProgress.color,
    [GRAPH_STATUSES.NotDue.label]: GRAPH_STATUSES.NotDue.color,
};

// Deliberately distinct from every GRAPH_STATUSES color — in particular from NotDue's #CDCCCC,
// which an unrecognized status used to borrow, making it indistinguishable from "Not Due" and
// leaving its white label unreadable on a light fill. Dark enough for white label text.
const UNKNOWN_STATUS_COLOR = '#6c757d';

// `colors`, `onClick` and `tooltip` are handed the computed datum (which carries `id`), whereas
// `labelTextColor` is handed the bar wrapper whose id lives under `data`. Normalize both shapes.
const resolveBarId = (bar: unknown): string => {
    const b = bar as { id?: string | number; data?: { id?: string | number } };
    return String(b.id ?? b.data?.id ?? '');
};

// One decimal only when the value is not whole, so the common case reads "25%" not "25.0%".
// Kept identical to Graph100StackedBar so the hover reads the same as it did before.
const formatPercentage = (percentage: number): string =>
    Number.isFinite(percentage) ? percentage.toFixed(1).replace(/\.0$/, '') : '0';

const NivoTaskCompletionBar = ({ data, onSegmentClick }: NivoTaskCompletionBarProps): ReactElement => {
    const validPoints = useMemo(() => (Array.isArray(data) ? data.filter((point) => point.value > 0) : []), [data]);

    // Percentages are of every status, not just the rendered ones — but zero-valued statuses
    // add nothing, so summing the visible points gives the same total.
    const total = useMemo(() => validPoints.reduce((sum, point) => sum + point.value, 0), [validPoints]);

    const orderedPoints = useMemo(() => {
        const known = statusOrder.flatMap((statusId) => validPoints.filter((point) => point.id === statusId));
        const unknown = validPoints.filter((point) => !statusOrder.includes(point.id));
        return [...known, ...unknown];
    }, [validPoints]);

    const segments = useMemo(
        () => orderedPoints.map((point, index) => ({ key: `seg_${index}`, point })),
        [orderedPoints]
    );

    const pointBySegmentKey = useMemo(() => {
        return segments.reduce<Record<string, TaskGraphDataPoint>>((acc, segment) => {
            acc[segment.key] = segment.point;
            return acc;
        }, {});
    }, [segments]);

    const keys = useMemo(() => segments.map((segment) => segment.key), [segments]);

    const chartData = useMemo(() => {
        if (keys.length === 0) return [];

        return [
            keys.reduce<Record<string, number | string>>((acc, key) => {
                const point = pointBySegmentKey[key];
                if (point) acc[key] = point.value;
                return acc;
            }, { bucket: 'tasks' }),
        ];
    }, [keys, pointBySegmentKey]);

    if (chartData.length === 0) {
        return <div className='nivo-task-completion-bar__empty' />;
    }

    return (
        <div className='nivo-task-completion-bar'>
            <div className='nivo-task-completion-bar__clip'>
                <ResponsiveBar
                    data={chartData}
                    keys={keys}
                    indexBy='bucket'
                    layout='horizontal'
                    margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                    padding={0}
                    innerPadding={0}
                    groupMode='stacked'
                    animate={false}
                    enableGridX={false}
                    enableGridY={false}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={null}
                    axisLeft={null}
                    label={(bar) => `${bar.value}`}
                    labelSkipWidth={16}
                    labelSkipHeight={10}
                    labelTextColor={(bar) => {
                        const point = pointBySegmentKey[resolveBarId(bar)];
                        return point?.id === GRAPH_STATUSES.NotDue.label ? '#2f3b4a' : '#ffffff';
                    }}
                    colors={(bar) => {
                        const point = pointBySegmentKey[resolveBarId(bar)];
                        return (point && statusColors[point.id]) || UNKNOWN_STATUS_COLOR;
                    }}
                    borderRadius={0}
                    isInteractive
                    role='img'
                    ariaLabel='Task completion status breakdown'
                    onClick={(bar) => {
                        const point = pointBySegmentKey[resolveBarId(bar)];
                        if (point) onSegmentClick(point);
                    }}
                    tooltip={(bar) => {
                        const point = pointBySegmentKey[resolveBarId(bar)];
                        const percentage = total > 0 ? (Number(bar.value) / total) * 100 : 0;
                        return (
                            <div className='nivo-task-completion-bar__tooltip'>
                                <strong>{point?.id ?? bar.id}</strong>: {formatPercentage(percentage)}% ({bar.value})
                            </div>
                        );
                    }}
                    theme={{
                        labels: {
                            text: {
                                fontSize: 12,
                                fontWeight: 700,
                            },
                        },
                        tooltip: {
                            container: {
                                fontSize: 12,
                                borderRadius: 4,
                                padding: '6px 8px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            },
                        },
                    }}
                />
            </div>
        </div>
    );
};

export default NivoTaskCompletionBar;
